import { supabase } from "@/integrations/supabase/client";

/**
 * Wrap a Supabase error object as a real Error with a stack trace and the
 * original PostgrestError preserved on `cause`. Without this, `throw error`
 * sends a non-Error object (Supabase returns plain objects) which Sentry's
 * global rejection handler captures as
 * "Non-Error promise rejection captured with value: <message>" — losing the
 * stack and making the issue impossible to triage (see Sentry issue
 * TODOWITH-AI-FRONTEND-K).
 */
function wrapSupabaseError(
  label: string,
  err: { message?: string; code?: string } | null | undefined,
): Error {
  const message = err?.message ?? "Unknown Supabase error";
  const code = err?.code ? ` [${err.code}]` : "";
  // ES2020 target — Error's `cause` option is ES2022. Assign after
  // construction so we preserve the original PostgrestError for debugging
  // while staying within the project's lib target.
  const wrapped = new Error(`${label}${code}: ${message}`) as Error & { cause?: unknown };
  wrapped.cause = err;
  return wrapped;
}

export interface ProfileWithSubscription {
  id: string;
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  phone_number: string | null;
  subscription_id: string | null;
  was_pro: boolean;
  onboarding_completed: boolean;
  cookie_consent_analytics: boolean | null;
  cookie_consent_timestamp: string | null;
  role?: string;
  created_at: string;
  updated_at: string;
  subscription_name?: string;
  credit_limit?: number;
  billing_period?: string;
  subscription_status?: string;
  current_period_start?: string;
  current_period_end?: string;
  cancelled_plan_id?: string;
  discount_percent?: number;
  discount_charges_remaining?: number;
  scheduled_plan_id?: string;
  scheduled_billing_period?: string;
}

export async function fetchProfileWithSubscription(
  userId: string
): Promise<ProfileWithSubscription | null> {
  const { data, error } = await supabase
    .from("profiles")
    .select(`
      *,
      subscriptions (
        name,
        credit_limit,
        price
      )
    `)
    .eq("user_id", userId)
    .maybeSingle();

  if (error) throw wrapSupabaseError("fetchProfileWithSubscription:profiles", error);
  if (!data) return null;

  // Fetch subscription details from user_subscriptions
  const { data: userSub } = await supabase
    .from("user_subscriptions")
    .select("billing_period, status, current_period_start, current_period_end, plan_id, discount_percent, discount_charges_remaining, scheduled_plan_id, scheduled_billing_period")
    .eq("user_id", userId)
    .maybeSingle();

  return {
    ...data,
    subscription_name: data.subscriptions?.name,
    credit_limit: data.subscriptions?.credit_limit,
    billing_period: userSub?.billing_period ?? undefined,
    subscription_status: userSub?.status ?? undefined,
    current_period_start: userSub?.current_period_start ?? undefined,
    current_period_end: userSub?.current_period_end ?? undefined,
    cancelled_plan_id: userSub?.status === "cancelled" ? userSub?.plan_id : undefined,
    discount_percent: userSub?.discount_percent ?? undefined,
    discount_charges_remaining: userSub?.discount_charges_remaining ?? undefined,
    scheduled_plan_id: userSub?.scheduled_plan_id ?? undefined,
    scheduled_billing_period: userSub?.scheduled_billing_period ?? undefined,
  } as ProfileWithSubscription;
}

/**
 * Whitelist of profile columns a user is allowed to write directly via the
 * Supabase anon client. The actual security boundary is the WITH CHECK on
 * `public.profiles` (see `supabase/migrations/20260523101734_strengthen_profile_update_lockdown.sql`);
 * this whitelist is defense-in-depth so a server-only column (e.g. `role`) cannot
 * even reach Postgres from the client — and so TypeScript flags any future
 * caller that tries.
 *
 * Marketing-consent fields are intentionally writable because the in-product
 * opt-in flow (`src/pages/Profile/useMarketingOptIn.ts`) writes them with the
 * user's anon JWT.
 */
const USER_EDITABLE_PROFILE_COLUMNS = [
  "display_name",
  "avatar_url",
  "phone_number",
  "onboarding_completed",
  "cookie_consent_analytics",
  "cookie_consent_timestamp",
  "marketing_consent",
  "marketing_consent_at",
  "marketing_consent_declined_at",
  "marketing_consent_ip",
  "marketing_consent_text_version",
] as const;

export type UserEditableProfileColumn = (typeof USER_EDITABLE_PROFILE_COLUMNS)[number];

export type UserEditableProfileUpdate = {
  [K in UserEditableProfileColumn]?: unknown;
};

export async function updateProfile(
  userId: string,
  updates: UserEditableProfileUpdate
): Promise<void> {
  // Strip any column not on the whitelist before it reaches Postgres. RLS is
  // the actual security boundary; this just prevents accidental misuse and
  // gives TypeScript a chance to flag callers passing server-only columns.
  const safe: Record<string, unknown> = {};
  for (const key of USER_EDITABLE_PROFILE_COLUMNS) {
    if (key in updates && updates[key] !== undefined) {
      safe[key] = updates[key];
    }
  }
  if (Object.keys(safe).length === 0) return;

  const { error } = await supabase
    .from("profiles")
    .update(safe)
    .eq("user_id", userId);

  if (error) throw wrapSupabaseError("updateProfile", error);
}

export async function fetchCookieConsent(
  userId: string
): Promise<{ analytics: boolean | null; timestamp: string | null } | null> {
  const { data, error } = await supabase
    .from("profiles")
    .select("cookie_consent_analytics, cookie_consent_timestamp")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) throw wrapSupabaseError("fetchCookieConsent", error);
  if (!data) return null;

  return {
    analytics: data.cookie_consent_analytics,
    timestamp: data.cookie_consent_timestamp,
  };
}

export async function updateProfileSubscription(
  userId: string,
  subscriptionId: string
): Promise<void> {
  const { error } = await supabase
    .from("profiles")
    .update({ subscription_id: subscriptionId })
    .eq("user_id", userId);

  if (error) throw wrapSupabaseError("updateProfileSubscription", error);
}
